import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Empty, Btn } from "@/components/admin/ui";
import { toast } from "sonner";

export default function AdminReturns() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/returns").then(({ data }) => setRows(data.returns)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return null;

  const decide = async (id, action) => {
    setBusy(id + action);
    try { await api.put(`/admin/returns/${id}`, { action }); await load(); toast.success(`Return ${action}d`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const reimburseCOD = async (r) => {
    const remaining = Math.max(0, Number(r.codAmountCollected || 0) - Number(r.codReimbursedAmount || 0));
    const amount = window.prompt(`Amount to record as externally reimbursed (up to ₹${remaining.toFixed(2)}):`, remaining.toFixed(2));
    if (amount == null) return;
    const reason = window.prompt("Reason for reimbursement:");
    if (!reason?.trim()) return;
    const reimbursementReference = window.prompt("Bank transfer / cash reimbursement reference:");
    if (!reimbursementReference?.trim()) return;
    const evidenceNote = window.prompt("Evidence note (receipt or supporting record):");
    if (!evidenceNote?.trim()) return;
    if (!window.confirm(`Record ₹${Number(amount).toFixed(2)} as reimbursed? This records evidence only; it does not send money.`)) return;
    setBusy(r.id + "reimburse");
    try {
      await api.post(`/admin/returns/${r.id}/cod-reimbursement`, {
        amount: Number(amount), reason: reason.trim(),
        reimbursementReference: reimbursementReference.trim(), evidenceNote: evidenceNote.trim(),
      });
      await load();
      toast.success("COD reimbursement recorded. No money was sent by the application.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <AdminHeader title="Returns" subtitle="Return requests, reverse pickups & refunds" />
      <Card className="overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="p-3">Return</th><th className="p-3">Order</th><th className="p-3">Items</th>
              <th className="p-3">Status</th><th className="p-3">RVP AWB</th><th className="p-3">Refund</th><th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 align-top">
                <td className="p-3 font-mono text-xs">{r.returnNumber}</td>
                <td className="p-3 font-mono text-xs">{r.orderNumber}</td>
                <td className="p-3 text-xs">{(r.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</td>
                <td className="p-3 text-xs">{String(r.status || "").replace(/_/g, " ")}</td>
                <td className="p-3 font-mono text-xs">{r.rvp?.awb || "—"}</td>
                <td className="p-3 font-mono text-xs">
                  {r.codReimbursement
                    ? `${r.codReimbursement.status.replace(/_/g, " ")} · ₹${r.codReimbursement.amount} (not sent)`
                    : r.refund?.razorpayRefundId ? `${r.refund.status} · ₹${r.refund.amount}` : "—"}
                </td>
                <td className="p-3">
                  {r.status === "REQUESTED" && (
                    <div className="flex gap-2">
                      <Btn variant="accent" onClick={() => decide(r.id, "approve")} loading={busy === r.id + "approve"} data-testid={`approve-return-${r.returnNumber}`}>Approve</Btn>
                      <Btn variant="outline" onClick={() => decide(r.id, "reject")} loading={busy === r.id + "reject"}>Reject</Btn>
                    </div>
                  )}
                  {["APPROVED", "PICKUP_SCHEDULED", "IN_TRANSIT"].includes(r.status) && (
                    <Btn variant="outline" onClick={() => decide(r.id, "received")} loading={busy === r.id + "received"} data-testid={`received-return-${r.returnNumber}`}>Mark received</Btn>
                  )}
                  {r.orderPaymentMethod === "COD" && r.orderPaymentStatus === "PAID"
                    && ["APPROVED", "PICKUP_SCHEDULED", "IN_TRANSIT", "RECEIVED"].includes(r.status)
                    && !r.codReimbursement && (
                    <Btn variant="outline" onClick={() => reimburseCOD(r)}
                      loading={busy === r.id + "reimburse"}
                      data-testid={`record-cod-reimbursement-${r.returnNumber}`}>
                      Record COD reimbursement
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <Empty text="No return requests yet" />}
      </Card>
    </div>
  );
}